"use client";

import { Database, TrendingUp, Lightbulb, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  icon: Database,
  title: "Accurate Data Preparation",
  description: "We clean, organise, and validate your data so that reports and analysis are built on dependable information rather than unclear or inconsistent spreadsheets.",
  metric: "Focus: cleaner data, fewer reporting issues"
}, {
  icon: TrendingUp,
  title: "Clear Reporting Structure",
  description: "We create structured reports, calculations, and performance views that help your business understand results more easily and consistently.",
  metric: "Focus: better visibility and reporting clarity"
}, {
  icon: Lightbulb,
  title: "Practical Business Insight",
  description: "We analyse trends, variances, and key performance indicators to help identify risks, opportunities, and areas requiring management attention.",
  metric: "Focus: insight that supports better decisions"
}, {
  icon: Lock,
  title: "Secure & Confidential Handling",
  description: "We treat business data with care, confidentiality, and professionalism, ensuring that sensitive information is handled responsibly throughout the process.",
  metric: "Focus: confidentiality and trust"
}];
export default function Rationale() {
  const heading = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="14152" id="why-it-matters" className="text-foreground py-24 md:py-32 dark bg-accent">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div ref={heading.ref} className="lg:col-span-8 transition-all duration-[800ms] ease-out" style={{
          opacity: heading.isVisible ? 1 : 0,
          transform: heading.isVisible ? "translateY(0)" : "translateY(24px)",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
        }}>
            <p className="font-default text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
              Our Commitment
            </p>
            <h2 className="font-default font-normal text-foreground mt-5 text-4xl leading-[1.08] tracking-tight md:text-5xl lg:text-6xl">Built on reliable data and practical insight</h2>
            <p className="font-default text-muted-foreground mt-6 text-lg leading-[1.65]" style={{
            maxWidth: "520px"
          }}>We believe that strong business decisions begin with clear, accurate, and well-structured information. Our approach focuses on transforming complex data into reliable reporting, meaningful analysis, and practical insight that your business can actually use.</p>
          </div>
        </div>

        <div ref={grid.ref} className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2">
          {benefits.map((benefit, i) => {
          const Icon = benefit.icon;
          return <Card key={benefit.title} data-index={i} className="group bg-card border border-border hover:shadow-lg p-9 md:p-9 rounded-lg transition-all duration-300 ease-out hover:-translate-y-[3px]" style={{
            opacity: grid.isVisible ? 1 : 0,
            transform: grid.isVisible ? "translateY(0)" : "translateY(24px)",
            transition: `opacity 800ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms, transform 800ms cubic-bezier(0.22, 1, 0.36, 1) ${i * 90}ms, border-color 300ms ease-out, box-shadow 300ms ease-out`
          }}>
                <CardContent className="p-0 flex flex-col">
                  <Icon className="size-14 text-white transition-all duration-300 group-hover:scale-110" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="font-default font-medium text-foreground mt-7 text-2xl leading-tight tracking-tight">
                    {benefit.title}
                  </h3>
                  <p className="font-default text-muted-foreground mt-4 text-base leading-[1.7]">
                    {benefit.description}
                  </p>
                  <p className="mt-6 font-default text-secondary" style={{
                fontSize: "0.85rem",
                fontWeight: 500,
                letterSpacing: "0.005em"
              }}>
                    {benefit.metric}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}